import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Circle, CircleCheck, CircleOff, Package, X } from 'lucide-react';
import { Button, IconButton, IconTile, SearchPill, Sheet, Stepper } from '@/components/ui';
import type { Product } from '@/services/types';
import { formatCurrency } from '@/utils/formatters';
import { LOW_STOCK } from './ProductTile';

interface Props {
    product: Product | null;
    onClose: () => void;
    onPick: (variant: Product, qty: number) => void;
}

const sellable = (v: Product) => v.inStock && v.availableQty > 0;

/**
 * 04a/04c variant picker (docs/POS_VARIANT_API.md). A parent with variants is never sold itself;
 * each row is a full product with its own price and sellable stock. Same sheet for F&B and retail.
 */
export const VariantPickerModal: React.FC<Props> = ({ product, onClose, onPick }) => {
    const { t } = useTranslation();
    const variants = product?.variants ?? [];
    const [query, setQuery] = useState('');
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [qty, setQty] = useState(1);

    useEffect(() => {
        setQuery('');
        setQty(1);
        setSelectedId(variants.find(sellable)?.id ?? null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product?.id]);

    const shown = useMemo(() => {
        const q = query.trim().toLowerCase();
        return q ? variants.filter((v) => (v.variantName || v.name).toLowerCase().includes(q)) : variants;
    }, [variants, query]);
    const selected = variants.find((v) => v.id === selectedId) ?? null;

    return (
        <Sheet
            isOpen={product !== null}
            onClose={onClose}
            size="lg"
            header={
                <div className="mb-3 flex items-center gap-3">
                    <IconTile size={52} tone="sunken" src={product?.image}><Package /></IconTile>
                    <div className="min-w-0 flex-1">
                        <h2 className="truncate text-lg font-black leading-tight lg:text-[22px]">{product?.name}</h2>
                        <p className="text-[13px] text-ink-muted">{t('catalog.variantCount', { count: variants.length })}</p>
                    </div>
                    <IconButton variant="sunken" size="xs" label={t('common.close')} onClick={onClose}><X /></IconButton>
                </div>
            }
            footer={
                <div className="flex items-center gap-2.5">
                    <Stepper size="lg" value={qty} onChange={setQty} max={selected?.availableQty ?? 1} />
                    <Button variant="primary" size="lg" fullWidth disabled={!selected} onClick={() => selected && onPick(selected, qty)}>
                        {selected ? t('catalog.addToOrder', { amount: formatCurrency(selected.price * qty) }) : t('catalog.chooseVariant')}
                    </Button>
                </div>
            }
        >
            <div className="flex flex-col gap-2">
                <SearchPill size="sm" value={query} onChange={setQuery} placeholder={t('catalog.searchVariant')} className="mb-1" />
                {shown.map((v) => {
                    const ok = sellable(v);
                    const active = v.id === selectedId;
                    const Icon = !ok ? CircleOff : active ? CircleCheck : Circle;
                    return (
                        <button
                            key={v.id}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            disabled={!ok}
                            onClick={() => setSelectedId(v.id)}
                            className={clsx(
                                'flex items-center gap-3 rounded-md px-3.5 py-3 text-left',
                                active ? 'border-2 border-accent-500 bg-accentsoft' : ok ? 'border border-line-subtle bg-surface-card hover:border-line' : 'border border-dashed border-line bg-surface-sunken opacity-60',
                            )}
                        >
                            <Icon className={clsx('h-[21px] w-[21px] shrink-0', active ? 'text-accent-600' : ok ? 'text-line' : 'text-ink-muted')} />
                            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                                <span className={clsx('text-sm lg:text-base', active ? 'font-extrabold' : 'font-bold', !ok && 'line-through')}>{v.variantName || v.name}</span>
                                <span className={clsx('text-xs font-bold', ok ? 'text-ink-muted' : 'text-status-danger')}>
                                    {!ok ? t('catalog.outOfStock') : v.availableQty <= LOW_STOCK ? t('catalog.remaining', { count: v.availableQty }) : t('catalog.stockCount', { count: v.availableQty })}
                                </span>
                            </span>
                            <span className={clsx('text-sm lg:text-base font-black tnum', active && 'text-ink-brand')}>{formatCurrency(v.price)}</span>
                        </button>
                    );
                })}
            </div>
        </Sheet>
    );
};
export default VariantPickerModal;
